import "./App.css";
import "./color-constants.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import ChatBody from "./Core/Components/ChatBody/ChatBody";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Introduction from "./Core/Components/Introduction/Introduction";
import LoginComponent from "./Core/Components/Auth/LoginComponent/LoginComponent";
import Guide from "./Core/Components/Guide/Guide";
import Consent from "./Core/Components/Consent/Consent";
import DeviceReadinessConsent from "./Core/Components/Device_readiness_Consent/Device_readiness_Consent";
import DeviceReadinessIntro from "./Core/Components/Device_readiness_Intro/Device_readiness_Intro";
import DeviceReadinessForm from "./Core/Components/Device_readiness_Form/Device_readiness_Form";
import DeviceReadinessEnd from "./Core/Components/Device_readiness_End/Device_readiness_End";
import DeviceReadinessRecord from "./Core/Components/Device_readiness_Record/Device_readiness_Record";
import NetworkNotification from "./Shared/Components/NetworkNotification/NetworkNotification";
import serviceWorkerRegistration from "./serviceWorkerRegistration";
import RegisterComponent from "./Core/Components/Auth/RegisterComponent/RegisterComponent";
import RegisterSuccessComponent from "./Core/Components/Auth/RegisterSuccessComponent/RegisterSuccessComponent";
import { getDefaultLanguage, getDeployment, setDefaultLanguage } from "./Actions/Utils";
import { checkMiddleWareHealth } from "./Actions/ChatActions";
import StaticHTML from "./Core/Components/StaticHTML/StaticHTML";
import { trackEvent } from "./Actions/Utils";
import KbSelection from "./Core/Components/kbSelection/kbSelection";
import {offlineText, onlineText, retryText, tryAgainText} from "./NewConstants/NetworkConstants";
import { liveText, downText, retryLiveText, tryAgainLiveText } from "./NewConstants/MiddlewareConstants";
import './firebaseConfig';
import { SERVER_URL } from "./ENV";
import IndicLang from "./Core/Components/IndicLang/IndicLang";
import LanguageCheck from "./Core/Components/LanguageCheck/LanguageCheck";
import Splash from "./Core/Components/Splash/Splash";
import { deploymentNeGD } from "./NewConstants/DeploymentConstants";

function App() {
  const [selectedLanguage, setSelectedLanguage] = useState(
    getDefaultLanguage() || "hi"
  );
  const [textSize, setTextSize] = useState(1.125);
  const audioRef = useRef();
  const middlewareIntervalRef = useRef(null);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlinePopup, setShowOnlinePopup] = useState(false);
  const [isTryingToConnect, setIsTryingToConnect] = useState(false);
  const [showStatusBar, setShowStatusBar] = useState(false);
  const [showMiddlewarePopup, setShowMiddlewarePopup] = useState(null);
  const [middlewareLiveStatus, setMiddlewareLiveStatus] = useState(null);
  const [retryMiddlewareStatus, setRetryMiddlewareStatus] = useState(null);
  const [startTour, setStartTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const serverSchedullingFlag = process.env.REACT_APP_SERVER_SCHEDULING_FLAG === "true";

  const getCurrentHour = useCallback(() => {
    const now = new Date();
    return now.getHours();
  }, []);

  const isRestrictedTime = useCallback(() => {
    const hour = getCurrentHour();
    return hour >= 0 && hour < 8;
  }, [getCurrentHour]);

  const decreaseTextSize = useCallback((event) => {
    event.preventDefault();
    setTextSize((prevSize) => Math.max(prevSize - 0.125, 0.5));
    trackEvent("Font Size", "Click", "Decrease Font Size");
  }, []);

  const increaseTextSize = useCallback((event) => {
    event.preventDefault();
    setTextSize((prevSize) => Math.min(prevSize + 0.125, 3));
    trackEvent("Font Size", "Click", "Increase Font Size");
  }, []);

  const middlewareHealth = useCallback(async () => {
    const res = await checkMiddleWareHealth();
    setShowMiddlewarePopup(res);
    setMiddlewareLiveStatus(String(res));
    return res;
  }, []);

  const tryToReconnect = useCallback(() => {
    setIsTryingToConnect(true);
    const timeoutId = setTimeout(() => {
      if (navigator.onLine) {
        setIsOnline(true);
        setShowOnlinePopup(true);
      } else {
        setIsTryingToConnect(false);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  const tryToReconnectMiddleware = useCallback(async () => {
    setRetryMiddlewareStatus(true);
    const res = await checkMiddleWareHealth();
    const timeoutId = setTimeout(() => {
      if (res) {
        setMiddlewareLiveStatus(String(res));
        setShowMiddlewarePopup(res);
        setRetryMiddlewareStatus(res);
      } else {
        setRetryMiddlewareStatus(false);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const onUpdate = (registration) => {
      if (window.confirm("New version available! Would you like to update?")) {
        const worker = registration.waiting;
        if (worker) {
          worker.postMessage({ action: "skipWaiting" });
          const stateChangeHandler = (event) => {
            if (event.target.state === "activated") {
              window.location.reload(true);
            }
          };
          worker.addEventListener("statechange", stateChangeHandler);
          return () => worker.removeEventListener("statechange", stateChangeHandler);
        }
      }
    };

    const registration = serviceWorkerRegistration({ onUpdate });
    return () => {
      if (registration && registration.unregister) {
        registration.unregister();
      }
    };
  }, []);

  useEffect(() => {
    if (middlewareLiveStatus === null) {
      middlewareHealth();
    }

    middlewareIntervalRef.current = setInterval(() => {
      middlewareHealth();
    }, 60000);

    return () => {
      if (middlewareIntervalRef.current) {
        clearInterval(middlewareIntervalRef.current);
      }
    };
  }, [middlewareHealth, middlewareLiveStatus]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlinePopup(true);
      setIsTryingToConnect(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOnlinePopup(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      const timer = setTimeout(() => {
        setShowOnlinePopup(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  useEffect(() => {
    setShowStatusBar(showOnlinePopup || isTryingToConnect || middlewareLiveStatus === "false");
  }, [showOnlinePopup, isTryingToConnect, middlewareLiveStatus]);

  useEffect(() => {
    setDefaultLanguage(selectedLanguage);
  }, [selectedLanguage]);

  const networkNotificationProps = useMemo(() => ({
    isOnline,
    isTryingToConnect,
    tryToReconnect,
    successText: onlineText[selectedLanguage],
    failureText: offlineText[selectedLanguage],
    tryingText: retryText[selectedLanguage],
    tryAgainText: tryAgainText[selectedLanguage]
  }), [isOnline, isTryingToConnect, tryToReconnect, selectedLanguage]);
  const middlewareNotificationProps = useMemo(() => ({
    isOnline: middlewareLiveStatus === "true",
    isTryingToConnect: retryMiddlewareStatus,
    tryToReconnect: tryToReconnectMiddleware,
    successText: liveText[selectedLanguage],
    failureText: showMiddlewarePopup === "modelDown" ? downText.modelDown[selectedLanguage] : 
                showMiddlewarePopup === "middlewareDown" ? downText.middlewareDown[selectedLanguage] : 
                downText.default[selectedLanguage],
    tryingText: retryLiveText[selectedLanguage],
    tryAgainText: tryAgainLiveText[selectedLanguage]
  }), [middlewareLiveStatus, retryMiddlewareStatus, tryToReconnectMiddleware, selectedLanguage, showMiddlewarePopup]);

  if (serverSchedullingFlag && isRestrictedTime()) {
    return <StaticHTML />;
  }

  return (
    <div translate="no" className={`h-screen w-screen overflow-hidden ${getDeployment() === deploymentNeGD ? 'iframe-theme' : 'pwa-theme'}`}>
      <Router>
        <Routes>
          <Route
            path="/chat"
            element={
              <ChatBody
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                textSize={textSize}
                decreaseTextSize={decreaseTextSize}
                increaseTextSize={increaseTextSize}
                audioRef={audioRef}
                stepIndex={stepIndex}
                setStepIndex={setStepIndex}
                startTour={startTour}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/"
            element={
              <Splash
                
              />
            }
          />
          <Route
            path="/lang"
            element={
              <IndicLang
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/login"
            element={
              <Introduction
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
              // <PhoneAuth />
            }
          />
          <Route
            path="/kb-selection"
            element={
              <KbSelection
                selectedLanguage={selectedLanguage}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/register"
            element={
              <RegisterComponent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/register-success"
            element={
              <RegisterSuccessComponent
                selectedLanguage={selectedLanguage}
              />
            }
          />
          <Route
            path="/guide"
            element={
              <Guide
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/consent"
            element={
              <Consent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
                setStepIndex={setStepIndex}
                setStartTour={setStartTour}
              />
            }
          />
          <Route
            path="/language-check"
            element={
              <LanguageCheck
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
              />
            }
          />
          <Route
            path="/device_readiness/consent"
            element={
              <DeviceReadinessConsent
                selectedLanguage={selectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/intro"
            element={
              <DeviceReadinessIntro
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/form"
            element={
              <DeviceReadinessForm
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/thanku"
            element={
              <DeviceReadinessEnd
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                audioRef={audioRef}
              />
            }
          />
          <Route
            path="/device_readiness/record"
            element={
              <DeviceReadinessRecord
                selectedLanguage={selectedLanguage}
              />
            }
          />
        </Routes>
      </Router>
      {(showOnlinePopup || isTryingToConnect) && (
        <NetworkNotification {...networkNotificationProps} />
      )}
      {showMiddlewarePopup !== null && (
        <NetworkNotification {...middlewareNotificationProps} />
      )}
      {SERVER_URL && /nonprod/i.test(SERVER_URL) && (
        <div className="non-prod-label">Non Prod</div>
      )}
    </div>
  );
}

export default App;
